import React, { useEffect, useState } from 'react';

import { App, Divider, Form, Input, Modal, Radio, Select, SelectProps, UploadFile, } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { MaskedInput } from 'antd-mask-input';

import { makeRawCnpj } from 'lib/helpers/Domain.helper';
import { makeFilterOption } from 'lib/helpers/Form.helper';
import { useAccount } from 'lib/providers/AccountContextProvider';
import { Show } from 'lib/Show';
import { sleep } from 'lib/Sleep';
import { UploadField } from 'lib/UploadField';
import { UrlField } from 'lib/UrlField';
import { createAccountClient } from 'services/Account.service';
import { listSuppliers } from 'services/Supplier.service';
import { listUser } from 'services/UserService';
import { Domain } from 'types/Domain.type';

type SelectOptions = NonNullable<SelectProps['options']>;

type Values = {
    name: string,
    email: string,
    phone: string,
    cnpj: string,
    url: string,
    logo: UploadFile[],
    client_user_responsible_id: Client.Model['client_user_responsible_id'] | null,
    supplier_id: Client.Model['supplier_id'] | null,
    external_resident_creation_workflow_type: 'auto' | 'approval',
    external_resident_creation_document_label: string,
}

export function ClientCreateModal() {
    const [isSending, setIsSending] = useState(false);
    const [users, setUsers] = useState<SelectOptions>([]);
    const [suppliers, setSuppliers] = useState<SelectOptions>([]); 
    const [isFetching, setIsFetching] = useState(false);

    const { setIsCreateClientModalVisible, fetchClients, clientManager } = useAccount();

    const [form] = Form.useForm<Values>();

    const watchedExternalResidentCreationWorkflowType = Form.useWatch('external_resident_creation_workflow_type', form);

    const app = App.useApp();

    const close = () => setIsCreateClientModalVisible(false);

    const defaultURL =
        ['unit', 'plan', 'trial'].includes(clientManager.contract)
            ? 'https://app.gcondo.com.br/client/'
            : 'https://sindigest.com.br/client/';

    useEffect(() => {
        const fetchData = async () => {
            setIsFetching(true);

            try {
                const usersResponse = await listUser();
                const suppliersResponse = await listSuppliers();

                if (usersResponse.success) {
                    const usersData = usersResponse.users.map((user: any) => ({
                        value: user.id,
                        label: user.name,
                    }));
                    setUsers(usersData);
                }

                if (suppliersResponse.success) {
                    const suppliersData = suppliersResponse.suppliers.map((supplier: any) => ({
                        value: supplier.id,
                        label: supplier.name,
                    }));
                    setSuppliers(suppliersData);
                }
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
            } finally {
                setIsFetching(false);
            }
        };

        fetchData();
    }, []);

    const onFinish = async ({
        phone,
        cnpj,
        external_resident_creation_document_label,
        ...values
    }: Values) => {
        setIsSending(true);

        const logo: string | null = values.logo ?
            values.logo[0].url
                ? values.logo[0].url
                : values.logo[0].response
            : null;

        const response = await createAccountClient({
            name: values.name,
            email: values.email,

            phone: phone !== undefined && phone !== ''
                ? phone
                : null,

            cnpj: cnpj !== undefined && cnpj !== ''
                ? makeRawCnpj(cnpj as Domain.Cnpj)
                : null,

            url: values.url,

            logo: logo,

            client_user_responsible_id: values.client_user_responsible_id,

            supplier_id: values.supplier_id ?? null,


            client_manager_id: clientManager.id,

            external_resident_creation_workflow_type: values.external_resident_creation_workflow_type,

            external_resident_creation_document_label: external_resident_creation_document_label !== undefined && external_resident_creation_document_label !== ''
                ? external_resident_creation_document_label
                : null,
        });

        setIsSending(false);
        if (response.type !== 'success') {
            app.notification.error({
                message: response.message || 'Erro',
                description: response.description || 'Ocorreu um erro inesperado.',
            });
            return;
        }

        app.notification.open({
            message: response.message || (response.type === 'success' ? 'Sucesso' : 'Erro'),
            description: response.description || 'Ocorreu um erro inesperado.',
            type: response.type || (response.type === 'success' ? 'success' : 'error'),
        });

        await sleep(1000);

        close();
        fetchClients();

        await sleep(2000);

        app.notification.info({
            message: 'Importante',
            description: 'Lembre-se que após criar um condomínio novo, você deve habilitar o acesso de sua equipe no módulo de "Usuários".',
            placement: 'top',
            duration: 5
        });
    };

   
    return (
        <Modal
            open
            title="Cadastrar condomínio"
            confirmLoading={isSending}
            onOk={form.submit}
            okText="Cadastrar"
            onCancel={close}
            cancelText="Cancelar"
        >
            <Divider />

            <Form
                form={form}
                onFinish={onFinish}
                name="createClient"
                layout="vertical"
                autoComplete="off"
            >
                <Form.Item> 
                    <UploadField
                        name="logo"
                        type='picture-card'
                        buttonText='Logo'
                        maxCount={1}
                        multiple={false}
                    />
                </Form.Item>

                <Form.Item
                    name="name"
                    label="Nome"
                    rules={[{ required: true, message: 'Por favor, digite um nome.' }]}
                >
                    <Input maxLength={500} />
                </Form.Item>

                <Form.Item
                    name="cnpj"
                    label="CNPJ"
                >
                    <MaskedInput mask="00.000.000/0000-00" />
                </Form.Item>


                <Form.Item
                    name="email"
                    label="E-mail"
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="phone"
                    label="Telefone"
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="user"
                    label="Síndico responsável"
                >
                    <Select
                        options={users}
                        loading={isFetching}
                        allowClear
                        showSearch
                        filterOption={makeFilterOption()}
                    />
                </Form.Item>

                <Form.Item
                    name="supplier_id"
                    label="Administradora"
                >
                    <Select
                        options={suppliers}
                        loading={isFetching}
                        allowClear
                        showSearch
                        filterOption={makeFilterOption()}
                    />
                </Form.Item>

                <Form.Item>
                    <UrlField
                        name="url"
                        baseUrl={defaultURL}
                        rules={[
                            {
                                pattern: /^[a-z0-9-]+$/,
                                message: 'A URL deve conter apenas letras minúsculas, números e hífens.',
                            },
                        ]}
                    />
                </Form.Item>

                <Form.Item
                    name="external_resident_creation_workflow_type"
                    label="Tipo de fluxo para criação de morador"
                    rules={[{ required: true, message: 'Por favor, selecione um tipo.' }]}
                >
                    <Radio.Group>
                        <Radio value="auto">Automático</Radio>
                        <Radio value="approval">Aprovação</Radio>
                    </Radio.Group>
                </Form.Item>

                <Show when={watchedExternalResidentCreationWorkflowType === 'approval'}>
                    <Form.Item
                        name="external_resident_creation_document_label"
                        label="Legenda do campo de documentos na criação de morador"
                        rules={[{ required: true, message: 'Por favor, digite uma legenda.' }]}
                        help={'Quando o fluxo para criação de morador for "Aprovação", um campo para envio de documentos será exibido.'}
                    >
                        <TextArea rows={5} />
                    </Form.Item>
                </Show>
            </Form>
        </Modal>
    );
}

